import { Container } from '@mui/material';
import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Divider,
  HeaderBreadcrumbs,
  NumberLabel,
  Stack,
  Tab,
  Tabs,
  YearNavigationBlock,
} from '@pflegenavi/web-components';
import { fmAppMainPages } from '@pflegenavi/frontend/routing';
import type {
  TransactionInitiatedBy,
  TransactionSourceType,
} from '@pflegenavi/shared/api';
import { TransactionStatusColors } from '@pflegenavi/shared/api';
import { useLocale } from '@pflegenavi/frontend/localization';
import {
  PaymentStatusValues,
  type UseFmPaymentsTableResult,
} from '../hooks/useFmPaymentsTable';

import { FmPaymentsToolbar } from '../components/payments/FmPaymentsToolbar';
import { FmPaymentsTable } from '../components/payments/FmPaymentsTable';
import { FMPendingChargesCard } from '../components/payments/FMPendingChargesCard';
import type { PendingCharge } from '@pflegenavi/frontend/api-nursing-home';
import { fmApplyPaymentsSortFilter } from '../helpers/fmPaymentsTableFilter';

type SimplifiedPendingCharge = Pick<
  PendingCharge,
  | 'charge_date'
  | 'payment_initiation'
  | 'amount'
  | 'pending_charge_status'
  | 'source_of_mandate'
  | 'family_member_first_name'
  | 'family_member_last_name'
>;

export interface SearchablePayment {
  id: string;
  type: string;
  serviceProvider: string | undefined;
  date: Date;
  initiationDate?: Date;
  amount: number;
  sourceType: TransactionSourceType;
  sourceTypeString: string;
  paymentStateString: string | undefined;
  disputed: boolean;
  initiatedBy: TransactionInitiatedBy;
  initiatedByName?: string;
  cancelled?: boolean;
  ibanLast4?: string | null;
  sequenceID: string;
}

export interface PaymentsPageViewProps {
  // Loading states
  isResidentPending: boolean;
  isPaymentsLoading: boolean;
  isPaymentsError: boolean;

  // Data props
  residentName?: string;
  nursingHomeName: string;
  currentBalance: number;
  pendingCharges: SimplifiedPendingCharge[];
  payments: SearchablePayment[] | undefined;

  // Table props
  tableProps: UseFmPaymentsTableResult;
  filterTerms: string;
  setFilterTerms: (terms: string) => void;
  reportingYear: Date;
  setReportingYear: (year: Date) => void;
}

export const PaymentsPageView: FC<PaymentsPageViewProps> = ({
  isPaymentsLoading,
  isPaymentsError,
  nursingHomeName,
  currentBalance,
  pendingCharges,
  payments,
  filterTerms,
  setFilterTerms,
  reportingYear,
  setReportingYear,
  tableProps,
}) => {
  const { t } = useTranslation();
  const locale = useLocale();

  const dataFiltered = useMemo(() => {
    return fmApplyPaymentsSortFilter({
      dataFiltered: payments ?? [],
      comparator: tableProps.comparator,
      filterTerms: filterTerms,
      reportingYear,
      locale,
    });
  }, [payments, tableProps.comparator, filterTerms, reportingYear, locale]);

  const TABS = useMemo(() => {
    return [
      {
        value: PaymentStatusValues.ALL,
        label: t(`transaction-status.${PaymentStatusValues.ALL}`),
        color: TransactionStatusColors.ALL,
        count: dataFiltered?.length ?? 0,
      },
    ];
  }, [dataFiltered, t]);

  return (
    <Container sx={{ maxWidth: 1400 }}>
      <HeaderBreadcrumbs
        heading={t('payments.main')}
        links={[
          {
            name: t('payments.main'),
            href: fmAppMainPages.PAYMENTS,
          },
          { name: t('common.overview') },
        ]}
        sx={{
          pb: 0,
        }}
      />

      {/* Pending Charges Card */}
      {pendingCharges.length > 0 && (
        <FMPendingChargesCard
          pendingCharges={pendingCharges}
          currentBalance={currentBalance}
          nursingHomeName={nursingHomeName}
        />
      )}

      <Card sx={{ maxWidth: 1400 }}>
        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={PaymentStatusValues.ALL}
          sx={{
            px: 2,
            bgcolor: 'background.neutral',
          }}
        >
          {TABS.map((tab) => (
            <Tab
              disableRipple
              key={tab.value}
              value={tab.value}
              label={
                <Stack
                  spacing={1}
                  direction="row"
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <div>{tab.label}</div>
                  <NumberLabel color={tab.color} variant="ghost">
                    {tab.count}
                  </NumberLabel>
                </Stack>
              }
            />
          ))}
          <Stack
            sx={{
              flex: 1,
            }}
          />
          <Stack sx={{ justifyContent: 'center' }}>
            <YearNavigationBlock
              reportingYear={reportingYear}
              setReportingYear={setReportingYear}
            />
          </Stack>
        </Tabs>

        <Divider />
        <FmPaymentsToolbar
          filterTerms={filterTerms}
          onFilterTerms={setFilterTerms}
        />

        <FmPaymentsTable
          dataAvailableBeforeFilter={Boolean((payments ?? []).length > 0)}
          payments={dataFiltered}
          isError={isPaymentsError}
          isLoading={isPaymentsLoading}
          isFiltered={!!filterTerms}
          {...tableProps}
        />
      </Card>
    </Container>
  );
};
